import React, { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { DialogTitle } from '@mui/material';
import PropTypes, { number } from 'prop-types';
import toast from 'react-hot-toast';
import InsertChartOutlinedIcon from '@mui/icons-material/InsertChartOutlined';
// eslint-disable-next-line boundaries/element-types
import { useParams } from 'react-router-dom';
import CalculatingTransportWindow from './window';
// eslint-disable-next-line boundaries/element-types
import ExportDataBySensorsButton from '../../../../../features/export/ExportDataBySensors/button';
import useRequests from './hooks/useRequests';
import { useAppSelector } from '../../../../../app/store';
import reducerPath from '../../../../../app/reducerPath';
import getDomain from './utils/getDomain';
import getMaxMin from './utils/getMaxMin';

const CalculatingTransportChart = lazy(() => import('./CalculatingTransportChart'));

const isFieldVolumeNormal = (chartData) => {
  if (!chartData.transportVolumePoint.length || !chartData.minVolume) return true;

  const currentValue = chartData.transportVolumePoint?.[chartData.transportVolumePoint.length - 1].y;

  return currentValue >= chartData.minVolume && currentValue <= chartData.maxVolume;
};

function CalculatingTransport({ id, producerId, fieldName, transportVolumePoint }) {
  const [isOpen, setIsOpen] = useState(false);

  const { logisticTaskId } = useParams();

  const {
    data: { source },
  } = useAppSelector((state) => state[`${reducerPath.logisticTasksBalanceResource}/counter`]);

  const { maxVolume, minVolume } = source?.balanceCapacityParameters || {};

  const requsetChartData = useRequests({ taskId: logisticTaskId, producerId });

  const currentChartData = useMemo(() => {
    const currentData = {
      transportVolumePoint,
      maxVolume,
      minVolume,
      yDomain: getDomain({
        maxVolume,
        minVolume,
        ...getMaxMin(transportVolumePoint || []),
      }),
    };
    return transportVolumePoint?.length ? { ...requsetChartData, ...currentData } : requsetChartData;
  }, [transportVolumePoint, requsetChartData, source]);

  const handleClose = useCallback(() => setIsOpen(false), [setIsOpen]);
  const handleOpen = useCallback(() => {
    if (producerId) {
      setIsOpen(true);
    } else {
      toast.error('ID гео-зоны были утеряны, начните процесс заново.');
    }
  }, [setIsOpen, producerId]);

  const chart = useMemo(() => <CalculatingTransportChart chartData={currentChartData} />, [currentChartData]);

  const iconButton = useMemo(() => <InsertChartOutlinedIcon />, []);

  const iconColor = isFieldVolumeNormal(currentChartData) ? 'rgba(0, 0, 0, 0.54)' : 'rgba(255, 0, 0, 0.692)';

  useEffect(() => {
    if (!id && isOpen) {
      toast.error('ID карточки были утеряны, начните процесс заново.');

      setIsOpen(false);
    }
  }, [isOpen, id]);

  return (
    <>
      <Suspense>
        <CalculatingTransportWindow
          title={
            <DialogTitle sx={{ p: 0, m: 0 }}>
              {fieldName}
              <br />
              Транспортный объем, т
            </DialogTitle>
          }
          isOpen={isOpen}
          onClose={handleClose}
        >
          {chart}
        </CalculatingTransportWindow>
      </Suspense>

      <ExportDataBySensorsButton
        tooltipTitle="Транспортный объем"
        disabled={!id}
        onClick={handleOpen}
        sx={{ padding: 0, color: iconColor }}
      >
        {iconButton}
      </ExportDataBySensorsButton>
    </>
  );
}

CalculatingTransport.propTypes = {
  id: number.isRequired,
  producerId: number.isRequired,
  fieldName: PropTypes.string.isRequired,
  transportVolumePoint: PropTypes.arrayOf(PropTypes.shape()).isRequired,
};

export default React.memo(CalculatingTransport);
